import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { IconType } from "react-icons";
import "../styles/MapPage.css";
import { FaChevronRight } from "react-icons/fa";
import { MenuService } from "../services/MenuService";
import { useAuth } from "../context/AuthContext";
import { availableIcons } from "../data/AvailableIcons";

interface SiteMenuProps {
  activities: { label: string; path: string }[];
  onSelectSubDomain?: (
    subLabel: string,
    idFenetre?: number,
    idSousFenetre?: number
  ) => void;
}

export const stringToIcon = (
  iconName?: string,
  size = 20,
  className = "me-2 text-success"
) => {
  if (!iconName) return null;

  const found = availableIcons.find(item => item.iconName === iconName);
  if (!found) return null;

  const IconComponent = found.icon as React.ComponentType<any>;

  return <IconComponent size={size} className={className} />;
};

const SiteMenu: React.FC<SiteMenuProps> = ({ activities, onSelectSubDomain }) => {
  const SafeIcon: React.FC<{ icon?: IconType; className?: string; size?: number }> = ({
    icon,
    className,
    size,
  }) => {
    if (!icon) return null;
    const IconComp = icon as React.ComponentType<any>;
    return <IconComp className={className} size={size} />;
  };

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();

  const subParam = searchParams.get("sub") || "";
  const [activeSubDomain, setActiveSubDomain] = useState<string>(subParam);
  const [menuBoxRetracted, setMenuBoxRetracted] = useState<boolean>(false);

  const [menus, setMenus] = useState<any[]>([]);
  const [openDomaines, setOpenDomaines] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    setActiveSubDomain(subParam);
  }, [subParam]);

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        if (api) {
          const menusData = await MenuService.getAllMenuDetailsIsActive(api);
          setMenus(menusData);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des menus :", error);
      }
    };
    fetchMenus();
  }, [api]);

  const handleSubDomainClick = (
    label: string,
    idFenetre?: number,
    idSousFenetre?: number
  ) => {
    navigate(`/?sub=${encodeURIComponent(label)}`);
    setActiveSubDomain(label);
    if (onSelectSubDomain) {
      onSelectSubDomain(label, idFenetre, idSousFenetre);
    }
  };

  // Composant récursif
  const SubMenu: React.FC<{
    items: any[];
    level?: number;
    activeSubDomain: string;
    onSelect: (label: string, idFenetre?: number, idSousFenetre?: number) => void;
    parentIdFenetre?: number;
  }> = ({ items, level = 1, activeSubDomain, onSelect, parentIdFenetre }) => {
    const [openIndices, setOpenIndices] = useState<{ [key: number]: boolean }>({});

    const toggle = (index: number) => {
      setOpenIndices(prev => {
        const newState: { [key: number]: boolean } = {};
        Object.keys(prev).forEach(k => (newState[+k] = false));
        newState[index] = !prev[index];
        return newState;
      });
    };

    return (
      <div className="sub-menu-wrapper mt-1" style={{ paddingLeft: `${level * 10}px`, opacity: 0.9 }}>
        {items.map((item, idx) => {
          const hasChildren = item.sousSousDomaines?.length > 0;
          const isOpen = openIndices[idx] || false;

          let currentIdFenetre: number | undefined;
          let currentIdSousFenetre: number | undefined;

          if (level === 1) {
            currentIdFenetre = item.id;
            currentIdSousFenetre = undefined;
          } else {
            currentIdFenetre = parentIdFenetre;
            currentIdSousFenetre = item.id;
          }

          return (
            <div key={item.id || idx}>
              <button
                className={`sub-menu-item w-100 text-start ${activeSubDomain === item.label ? "active" : ""}`}
                onClick={() => {
                  if (hasChildren) {
                    toggle(idx);
                  } else {
                    if (level === 1) {
                      onSelect(item.label, currentIdFenetre, undefined);
                    } else {
                      onSelect(item.label, currentIdFenetre, currentIdSousFenetre);
                    }
                  }
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span className="sub-menu-label">
                    {level === 1 ? "-" : "•"} {item.label}
                  </span>
                  {hasChildren && <span className={`arrow-icon ${isOpen ? "open" : ""}`}>&gt;</span>}
                </div>
              </button>

              {hasChildren && isOpen && (
                <SubMenu
                  items={item.sousSousDomaines}
                  level={level + 1}
                  activeSubDomain={activeSubDomain}
                  onSelect={onSelect}
                  parentIdFenetre={currentIdFenetre}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`menu-box card p-3 ${menuBoxRetracted ? "retracted" : ""}`}>
      <button
        className={`btn-retract ${menuBoxRetracted ? "" : "blink"}`}
        onClick={() => setMenuBoxRetracted(!menuBoxRetracted)}
        title={menuBoxRetracted ? "Afficher le menu" : "Rétrécir le menu"}
      >
        <SafeIcon icon={FaChevronRight} size={20} className={menuBoxRetracted ? "" : "rotate-180"} />
      </button>

      {/* Domaines depuis API */}
      <div className="domain-scroll-container">
        {menus.map((d, i) => {
          const isOpen = openDomaines[i] || false;
          const toggleMenu = () => {
            setOpenDomaines(prev => {
              const newState: { [key: number]: boolean } = {};
              Object.keys(prev).forEach(k => (newState[+k] = false));
              newState[i] = !prev[i];
              return newState;
            });
          };

          return (
            <div key={d.id || i}>
              <button
                className={`add-doc-item ${isOpen ? "selected" : ""}`}
                onClick={toggleMenu}
                style={{ width: "100%" }}
              >
                <div className="d-flex align-items-center justify-content-between" style={{ width: "300px" }}>
                  <div className="d-flex align-items-center">
                    {stringToIcon(d.iconName, 20, "fs-5 me-3 text-success")}
                    <span className="domain-label">{d.label}</span>
                  </div>
                  <span className={`arrow-icon ${isOpen ? "open" : ""}`}>&gt;</span>
                </div>
              </button>

              {isOpen && d.sousDomaines && (
                <SubMenu
                  items={d.sousDomaines}
                  activeSubDomain={activeSubDomain}
                  onSelect={handleSubDomainClick}
                  parentIdFenetre={d.id}
                />
              )}
            </div>
          );
        })}
      </div>
      
      <hr className="sub-menu-separator" />
      <div className="activity-menu mb-2">
        <span className="activity-label fw-bold">ACTIVITÉS</span>
      </div>

      {activities.map((item, index) => {
        const currentPath = location.pathname.replace(/\/+$/, "");
        const itemPath = item.path.startsWith("/") ? item.path : "/" + item.path;
        const isActive = currentPath === itemPath;

        return (
          <div className="activity-menu-wrapper" key={index}>
            <button
              className={`sub-menu-item w-100 text-start ${isActive ? "active" : ""}`}
              onClick={() => {
                if (!isActive) navigate(itemPath);
              }}
              disabled={isActive}
            >
              <span className="menu-label">{item.label}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default SiteMenu;